// ./src/hooks/useVenues.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Venue,
  VenueService,
  VenueFilter,
  VenueInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseVenuesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<VenueFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseVenuesReturn {
  // Data state
  venue: Venue | null;
  venues: Venue[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;
  hasVenues: boolean;

  // Search and filters
  filters: Partial<VenueFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingVenueId: string | null;
  selectedVenue: Venue | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<VenueFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Venue[]>;
  create: (params: VenueInput) => Promise<Venue | null>;
  update: (id: string, params: VenueInput) => Promise<Venue | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<VenueFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedVenue: (venue: Venue | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  venuesByStatus: Record<string, Venue[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useVenues = (options: UseVenuesOptions = {}): UseVenuesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [venue, setVenue] = useState<Venue | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // Search and filters
  const [filters, setFilters] = useState<Partial<VenueFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingVenueId, setRemovingVenueId] = useState<string | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(() => {
    return venues.length === 0 && !loading;
  }, [venues.length, loading]);

  // Check if there are any venues available
  const hasVenues = useMemo(() => {
    return !loading ? venues && venues.length > 0 : false;
  }, [venues, loading]);

  const venuesByStatus = useMemo(() => {
    return venues.reduce((acc, venue) => {
      const status = String(venue.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(venue);
      return acc;
    }, {} as Record<string, Venue[]>);
  }, [venues]);

  // Helper function to update venue in list if it exists, or add it if not
  const updateOrAddVenueInList = useCallback((updatedVenue: Venue) => {
    setVenues((prev) => {
      const existingIndex = prev.findIndex((v) => v.id === updatedVenue.id);
      if (existingIndex >= 0) {
        const newVenues = [...prev];
        newVenues[existingIndex] = updatedVenue;
        return newVenues;
      } else {
        return [updatedVenue, ...prev];
      }
    });
  }, []);

  // Helper function to remove venue from list if it exists
  const removeVenueFromList = useCallback((venueId: string) => {
    setVenues((prev) => prev.filter((v) => v.id !== venueId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the venues");
    setVenue(null);
    setVenues([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingVenueId(null);
    setSelectedVenue(null);
  }, []);

  // Get single venue (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await VenueService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const venueData = result.data[0] as Venue;
          setVenue(venueData);
          updateOrAddVenueInList(venueData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load venue";
          setLastError(errorMessage);
          setVenue(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load venue";
        setLastError(errorMessage);
        setVenue(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddVenueInList]
  );

  // List venues (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<VenueFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setVenues([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await VenueService.list(
          filter as VenueFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newVenues = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setVenues((prev) => {
              const existingIds = new Set(prev.map((v) => v.id));
              const uniqueNewVenues = newVenues.filter(
                (v) => !existingIds.has(v.id)
              );
              return [...prev, ...uniqueNewVenues];
            });
          } else {
            setVenues(newVenues);
          }

          setHasMore(newVenues.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load venues";
          setLastError(errorMessage);
          if (!isAppending) setVenues([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load venues";
        setLastError(errorMessage);
        if (!isAppending) setVenues([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many venues by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Venue[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await VenueService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedVenues = Array.isArray(result.data) ? result.data : [];
          loadedVenues.forEach((venue) => {
            updateOrAddVenueInList(venue);
          });
          setLastError(null);
          return loadedVenues;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load venues";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load venues";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddVenueInList]
  );

  // Create venue (matching API method name)
  const create = useCallback(
    async (params: VenueInput): Promise<Venue | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await VenueService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newVenue = result.data[0] as Venue;
          setVenue(newVenue);
          setVenues((prev) => [newVenue, ...prev]);
          setLastError(null);
          return newVenue;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create venue";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create venue";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update venue (matching API method name)
  const update = useCallback(
    async (id: string, params: VenueInput): Promise<Venue | null> => {
      if (!id) {
        setLastError("Venue ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await VenueService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedVenue = result.data[0] as Venue;
          setVenue(updatedVenue);
          updateOrAddVenueInList(updatedVenue);
          setLastError(null);
          return updatedVenue;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update venue";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update venue";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddVenueInList]
  );

  // Remove venue (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Venue ID is required for removal");
        return false;
      }

      setRemovingVenueId(id);
      setLastError(null);

      try {
        const result = await VenueService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setVenue(null);
          removeVenueFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove venue";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove venue";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingVenueId(null);
      }
    },
    [removeVenueFromList]
  );

  // Load more venues
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh venues
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  useEffect(() => {
    if (autoLoad && isAuthenticated && loadedForUserId != userId) {
      setLoadedForUserId(userId || null);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    isAuthenticated,
    list,
    pageSize,
    initialSort,
    userId,
    loadedForUserId,
  ]);

  return {
    venue,
    venues,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    hasVenues,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingVenueId,
    selectedVenue,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedVenue,
    reset,
    venuesByStatus,
    lastError,
    clearError,
  };
};
